<script lang="ts">
  /** Register or sign in. Two buttons, no password field anywhere. */
  import { checkUsername, describeUsernameProblem } from '$domain/username.ts';
  import { pairDevice, type PairingProgress } from './pairing.ts';
  import { isNative } from './platform.ts';
  import { describeError, session } from './session.svelte.ts';
  import InstallApp from './InstallApp.svelte';
  import Wordmark from './Wordmark.svelte';

  const { requireInvite, apk, wearApk }: {
    requireInvite: boolean;
    apk: { version: string; size: number } | null;
    wearApk: { version: string; size: number } | null;
  } = $props();

  let mode = $state<'signin' | 'register'>('signin');
  let username = $state('');
  let invite = $state('');
  let busy = $state(false);
  let error = $state('');
  let pairing = $state<PairingProgress | undefined>();

  // Validated with the very same module the server uses, so the message while typing is the
  // message the server would give. That is the whole reason `src/domain` is importable here.
  const usernameProblem = $derived.by(() => {
    if (username === '') return '';
    const checked = checkUsername(username);
    return checked.ok ? '' : describeUsernameProblem(checked.problem);
  });

  const canRegister = $derived(
    username !== '' && usernameProblem === '' && (!requireInvite || invite.trim() !== ''),
  );

  /**
   * The APK route (brief §11.7).
   *
   * A WebView has no credentials API, so there is no passkey button to press here. The
   * ceremony happens in a Custom Tab and the app waits for the code to be approved.
   */
  async function pair() {
    busy = true;
    error = '';
    pairing = undefined;
    try {
      const ok = await pairDevice('Android', (progress) => (pairing = progress));
      if (!ok) error = 'Die Kopplung ist abgelaufen. Versuch es noch einmal.';
      else await session.refresh();
    } catch (cause) {
      error = describeError(cause);
    } finally {
      busy = false;
    }
  }

  async function run(action: () => Promise<void>) {
    busy = true;
    error = '';
    try {
      await action();
    } catch (cause) {
      error = describeError(cause);
    } finally {
      busy = false;
    }
  }
</script>

<div class="wrap">
  <div class="card">
    <img src="/icon-192.png" alt="" width="72" height="72" />
    <h1><Wordmark height="2.6rem" /></h1>
    <p class="lead">Wo ist gerade was los?</p>

    {#if isNative()}
      <button class="primary" disabled={busy} onclick={pair}>
        {busy ? 'Warte auf die Freigabe …' : 'Anmelden'}
      </button>
      {#if pairing !== undefined}
        <p class="code">{pairing.code}</p>
        <p class="hint">
          Im Browser öffnet sich gerade die Freigabe. Der Code dort muss dieser sein.
        </p>
      {:else}
        <p class="hint">
          Passkeys gehen nur im echten Browser — die App öffnet ihn kurz und meldet sich danach
          selbst an.
        </p>
      {/if}
    {:else if mode === 'signin'}
      <button class="primary" disabled={busy} onclick={() => run(() => session.signIn())}>
        {busy ? 'Warte auf deinen Passkey …' : 'Anmelden'}
      </button>
      <p class="hint">Kein Passwort. Dein Gerät bestätigt, dass du es bist.</p>
      <button class="link" onclick={() => { mode = 'register'; error = ''; }}>
        Noch kein Konto? Registrieren
      </button>
    {:else}
      <label>
        <span>Dein Name</span>
        <input
          bind:value={username}
          autocapitalize="none"
          autocomplete="username"
          spellcheck="false"
          placeholder="z. B. soeren"
          maxlength="20"
        />
      </label>
      {#if usernameProblem}<p class="problem">{usernameProblem}</p>{/if}

      {#if requireInvite}
        <label>
          <span>Einladungscode</span>
          <input
            bind:value={invite}
            autocapitalize="characters"
            spellcheck="false"
            placeholder="A7K2M-9PQXR"
          />
        </label>
      {/if}

      <button
        class="primary"
        disabled={busy || !canRegister}
        onclick={() =>
          run(() => session.register(username, requireInvite ? invite : undefined))}
      >
        {busy ? 'Warte auf deinen Passkey …' : 'Passkey anlegen'}
      </button>
      <button class="link" onclick={() => { mode = 'signin'; error = ''; }}>
        Ich habe schon ein Konto
      </button>
    {/if}

    {#if error}<p class="error" role="alert">{error}</p>{/if}

    <InstallApp
      {apk}
      {wearApk}
      reason="Die App warnt dich auch, wenn sie geschlossen ist — im Browser geht das nicht."
    />
  </div>
</div>

<style>
  .wrap {
    display: grid;
    place-items: center;
    min-height: 100%;
    padding: 1.5rem;
  }

  .card {
    width: min(24rem, 100%);
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    align-items: center;
    padding: 2rem 1.5rem;
    border: 1px solid var(--border);
    border-radius: 1.25rem;
    background: var(--card);
    text-align: center;
  }

  img {
    border-radius: 1rem;
  }

  h1 {
    margin: 0;
    line-height: 0;
  }

  .lead {
    margin: 0 0 0.5rem;
    color: var(--muted);
  }

  label {
    width: 100%;
    text-align: left;
    display: grid;
    gap: 0.25rem;
    font-size: 0.8125rem;
    color: var(--muted);
  }

  input {
    width: 100%;
    padding: 0.65rem 0.75rem;
    border: 1px solid var(--border);
    border-radius: 0.6rem;
    background: var(--bg);
    color: var(--fg);
    font-size: 1rem;
  }

  .primary {
    width: 100%;
    padding: 0.8rem 1rem;
    border: 0;
    border-radius: 0.75rem;
    background: var(--accent);
    color: #fff;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
  }

  .primary:disabled {
    opacity: 0.55;
    cursor: default;
  }

  .link {
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 0.875rem;
    text-decoration: underline;
    cursor: pointer;
  }

  .code {
    margin: 0;
    padding: 0.6rem;
    border: 1px dashed var(--border);
    border-radius: 0.6rem;
    font-family: ui-monospace, monospace;
    font-size: 1.25rem;
    letter-spacing: 0.25em;
    text-align: center;
  }

  .hint {
    margin: 0;
    font-size: 0.8125rem;
    color: var(--muted);
  }

  .problem,
  .error {
    margin: 0;
    font-size: 0.8125rem;
    color: var(--accent);
  }
</style>
